import {
  type GlobalAdminsAccessEditAccessModePermission,
  type GlobalRolePermissions,
  type GlobalRolesAccessEditAccessModePermission,
  type NestedGlobalPermissions,
  type SocPortalAccessEditAccessModePermission,
  type SpecialPermissions,
  type WorkspaceSubscriptionTypeAccess,
  type WorkspaceTypeAccess,
} from "@/_store/roles.module";
import { toRaw } from "vue";
import get from "lodash/get";
import {
  GlobalRoleScopeSection,
  MspPortalScopeSection,
  RolePermissionAccessMode,
  RolePermissionsScope,
  WorkspaceManagementScopeSections,
} from "@/constants/roles";
import { usePermissionsStore } from "@/_store/permissions.module.ts";

/**
 * Resolves the global permission entry and returns true when the action must be blocked:
 * entry missing, string access mode is not `EDIT`, nested `accessMode` is not `EDIT`,
 * or — when `nestedPermissionKey` is given — the matching `editAccessModePermission` flag is falsy.
 * @param scopeName
 * @param key
 * @param nestedPermissionKey
 */
export function isGlobalActionRestricted<
  T extends keyof GlobalRolePermissions,
  K extends keyof GlobalRolePermissions[T],
  V extends (GlobalRolePermissions[T][K] extends NestedGlobalPermissions
    ? NonNullable<GlobalRolePermissions[T][K]["editAccessModePermission"]>
    : GlobalRolePermissions[T][K]),
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  const permissions = getGlobalPermissionsByScopeNameAndKey(scopeName, key);
  // Filter case when permissions doesn't exist
  if (!permissions) return true;

  const isEditAccessModePermission = (
    _permission:
      | NestedGlobalPermissions
      | WorkspaceSubscriptionTypeAccess
      | WorkspaceTypeAccess
      | SpecialPermissions
  ): _permission is NestedGlobalPermissions => {
    if (scopeName === RolePermissionsScope.TICKETS) {
      return true;
    }
    return [
      GlobalRoleScopeSection.GLOBAL_ADMIN_USERS,
      GlobalRoleScopeSection.GLOBAL_ROLES,
      GlobalRoleScopeSection.SOC_PORTAL,
      MspPortalScopeSection.MSP_WORKSPACES,
      MspPortalScopeSection.MSP_ROLES,
      MspPortalScopeSection.MSP_ADMIN_USERS,
      WorkspaceManagementScopeSections.ADMIN_USERS,
    ].includes(key as GlobalRoleScopeSection);
  };

  // Filter case when permissions is an object of such shape
  // {
  //   accessMode: RolePermissionAccessMode;
  //   editAccessModePermission?: {[key: string]: boolean}
  // };
  if (typeof permissions === "string") {
    return permissions !== RolePermissionAccessMode.EDIT;
  }

  if (isEditAccessModePermission(permissions)) {
    // If no key provided, then check if it's edit or not
    if (!nestedPermissionKey) {
      return permissions.accessMode !== RolePermissionAccessMode.EDIT;
    }

    // If key is provided, then check the particular action
    if (
      nestedPermissionKey &&
      permissions.accessMode === RolePermissionAccessMode.EDIT &&
      permissions.editAccessModePermission
    ) {
      return permissions.editAccessModePermission
        ? !permissions.editAccessModePermission[
            nestedPermissionKey as keyof (
              | GlobalAdminsAccessEditAccessModePermission
              | GlobalRolesAccessEditAccessModePermission
              | SocPortalAccessEditAccessModePermission
            )
          ]
        : true;
    }
  } else {
    return !permissions[
      nestedPermissionKey as keyof (
        WorkspaceSubscriptionTypeAccess | WorkspaceTypeAccess | SpecialPermissions
      )
    ];
  }

  return true;
}

/**
 * Returns true when the global permission entry for the given scope/key resolves to `NO_ACCESS`
 * (either as a plain string or as a nested object's `accessMode`). Safe-defaults to `true`
 * when the entry is missing or has no `accessMode` field, so the UI hides by default.
 * @param scopeName
 * @param key
 */
export function isGlobalAccessRestricted<
  T extends keyof GlobalRolePermissions,
  K extends keyof GlobalRolePermissions[T] = keyof GlobalRolePermissions[T],
>(scopeName: T, key: K) {
  const permissions = getGlobalPermissionsByScopeNameAndKey(scopeName, key);
  if (!permissions) return true;

  if (typeof permissions === "string") {
    return permissions === RolePermissionAccessMode.NO_ACCESS;
  }

  return (permissions as NestedGlobalPermissions).accessMode
    ? (permissions as NestedGlobalPermissions).accessMode === RolePermissionAccessMode.NO_ACCESS
    : true;
}

/**
 * Reads the raw global permission entry at `[scopeName][key]` from the permissions store,
 * unwrapping the reactive proxy with `toRaw` before the `lodash/get` lookup. Returns the value
 * as stored (nested object, access-mode string, or `undefined` when absent) without interpreting it.
 * @param scopeName
 * @param key
 */
export function getGlobalPermissionsByScopeNameAndKey<
  T extends keyof GlobalRolePermissions = keyof GlobalRolePermissions,
  K extends keyof GlobalRolePermissions[T] = keyof GlobalRolePermissions[T],
>(scopeName: T, key: K) {
  const permissionsStore = usePermissionsStore();
  const globalRolePermissions = toRaw(permissionsStore.globalWorkspacePermissions);
  return get(globalRolePermissions, [scopeName, key]) as
    | NestedGlobalPermissions
    | WorkspaceSubscriptionTypeAccess
    | WorkspaceTypeAccess
    | SpecialPermissions
    | RolePermissionAccessMode
    | undefined;
}
